import React, { useMemo, useState } from 'react';

import type {
  EditingMode,
  IndentationKey,
  ParagraphElement,
  TableCellElement,
} from '@aline/common';
import {
  inToPx,
  isParagraphElement,
  isTableCellElement,
  pageContentWidth,
  pxToIn,
} from '@aline/common';
import { SvgIcon, useTheme } from '@mui/material';
import { equals } from 'ramda';
import { Editor, Range } from 'slate';
import { ALWAYS_VISIBLE_SCROLLBAR, CUSTOM_SCROLLBAR_WIDTH } from 'src/components';
import { useEditorTabStateSelector } from 'src/editor/editor-tab-state-registry';
import { Queries } from 'src/editor/queries';
import { useWalkthroughActive } from 'src/editor/toolbars/editor-app-bars/helpers';
import { Ticks } from 'src/editor/toolbars/rulers/Ticks';
import {
  roundToOneSixteenthOfAnInch,
  useDragAndDrop,
  useMarginAdjuster,
} from 'src/editor/toolbars/rulers/utils';
import { useEditorViewPreferences } from 'src/editor/vars';
import { useCurrentUserId } from 'src/gql';
import { getAuthorizedTransformsForEditor } from 'src/hooks';
import { useEditor, useEditorLoading } from 'src/layout-engine';
import { useEditingMode } from 'src/store/document/selectors';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  indentationMarker: {
    bottom: 1,
    height: 7,
    left: -6,
    position: 'absolute',
    width: 13,
  },
  line: {
    backgroundColor: theme.appColors.primary,
    left: 0,
    position: 'absolute',
    top: 1,
    width: 1,
    zIndex: theme.zIndex.appBar,
  },
  lineContainer: {
    margin: '0 auto 0 auto',
    position: 'relative',
  },
  outerPageArea: {
    display: 'flex',
    height: 16,
    justifyContent: 'center',
    margin: '0 auto',
    overflow: 'hidden',
  },
  pageArea: {
    height: '100%',
    position: 'relative',
  },
  root: {
    backgroundColor: theme.appColors.rulerMargin,
    borderBottom: `1px ${theme.appColors.rule} solid`,
    /** Shift the ruler left to account for the scrollbar (fixes EDI-295). */
    paddingRight: ALWAYS_VISIBLE_SCROLLBAR ? CUSTOM_SCROLLBAR_WIDTH : 0,
  },
  skeletonRoot: {
    backgroundColor: theme.appColors.rulerMargin,
    borderBottom: `1px ${theme.appColors.rule} solid`,
    height: '24px',
  },
}));

export function HorizontalRuler(): JSX.Element | null {
  const { classes } = useStyles();
  const currentUserId = useCurrentUserId();
  const { commenting, viewing } = useEditingMode();
  const { walkThroughActive } = useWalkthroughActive();
  const [rootNode, setRootNode] = useState<HTMLDivElement | null>(null);

  const pageDimensions = useEditorTabStateSelector(
    state => state.derived.documentProperties.pageDimensions,
    equals
  );
  const { showRulers } = useEditorViewPreferences();
  const editorLoading = useEditorLoading();

  /**
   * Calculate Margins
   */
  const { dragLeftMargin, leftMarginDelta, leftMarginDragPosition } = useDragLeftMargin();
  const { dragRightMargin, rightMarginDelta, rightMarginDragPosition } = useDragRightMargin();

  /**
   * Calculate Indentation Markers
   */
  const { dragIndentationLeft, indentationLeftDelta, leftMarkerDragPosition } =
    useDragIndentationLeft(leftMarginDelta ?? 0);
  const { dragIndentationRight, indentationRightDelta, rightMarkerDragPosition } =
    useDragIndentationRight(rightMarginDelta ?? 0);
  const { dragFirstLineIndent, firstLineIndentDelta, firstLineMarkerDragPosition } =
    useDragFirstLineIndent(leftMarkerDragPosition);

  const pageWidth = pageDimensions.width;
  const contentWidth = pageWidth - leftMarginDragPosition - rightMarginDragPosition;
  const dragLinePosition = useMemo(() => {
    if (leftMarginDelta !== null) return leftMarginDragPosition;
    if (rightMarginDelta !== null) return pageDimensions.marginLeft + contentWidth;
    if (indentationLeftDelta !== null) return leftMarkerDragPosition;
    if (indentationRightDelta !== null) return rightMarkerDragPosition;
    if (firstLineIndentDelta !== null) return firstLineMarkerDragPosition;

    return null;
  }, [
    contentWidth,
    firstLineIndentDelta,
    firstLineMarkerDragPosition,
    indentationLeftDelta,
    indentationRightDelta,
    leftMarginDelta,
    leftMarginDragPosition,
    leftMarkerDragPosition,
    pageDimensions.marginLeft,
    rightMarginDelta,
    rightMarkerDragPosition,
  ]);

  const hideRuler = walkThroughActive || !showRulers || viewing || commenting || !currentUserId;
  if (hideRuler) return null;

  if (editorLoading) return <HorizontalRulerSkeleton />;

  return (
    <div className={classes.root}>
      <div ref={setRootNode}>
        <div className={classes.outerPageArea} style={{ width: inToPx(pageWidth) + 16 }}>
          <div className={classes.pageArea} style={{ width: inToPx(pageWidth) }}>
            <Ticks
              contentSize={contentWidth}
              direction="horizontal"
              inchTickCount={Math.ceil(pageWidth)}
              marginStart={leftMarginDragPosition}
              onEndDrag={dragRightMargin}
              onStartDrag={dragLeftMargin}
            />
            <IndentationMarker
              name="left"
              position={leftMarkerDragPosition}
              onMouseDown={(event): void => {
                event.preventDefault();
                dragIndentationLeft(event.clientX);
              }}
            />
            <IndentationMarker
              name="first-line"
              position={firstLineMarkerDragPosition}
              style={{ bottom: 8 }}
              variant="first-line"
              onMouseDown={(event): void => {
                event.preventDefault();
                dragFirstLineIndent(event.clientX);
              }}
            />
            <IndentationMarker
              name="right"
              position={rightMarkerDragPosition}
              onMouseDown={(event): void => {
                event.preventDefault();
                dragIndentationRight(event.clientX);
              }}
            />
          </div>
        </div>
        {rootNode && dragLinePosition !== null && (
          <div className={classes.lineContainer} style={{ width: inToPx(pageWidth) }}>
            <div
              className={classes.line}
              style={{
                height: `calc(100vh - ${rootNode.getBoundingClientRect().top + 18}px)`,
                transform: `translateX(${inToPx(dragLinePosition)}px)`,
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export function HorizontalRulerSkeleton() {
  const { classes } = useStyles();

  return <div className={classes.skeletonRoot}></div>;
}

interface IndentationMarkerProps {
  name: string;
  onMouseDown(event: React.MouseEvent): void;
  position: number;
  path?: string;
  style?: {
    bottom?: number;
  };
  variant?: 'first-line' | 'default';
}

function IndentationMarker({
  name,
  onMouseDown,
  position,
  style,
  variant = 'default',
}: IndentationMarkerProps): JSX.Element {
  const theme = useTheme();
  const { classes } = useStyles();
  const editorLoading = useEditorLoading();

  const firstLineIndentMark = (
    <rect
      fill={editorLoading ? theme.palette.action.disabled : theme.palette.primary.main}
      height="3"
      rx="1.5"
      width="10"
      x="1"
      y="2.5"
    />
  );
  const leftRightIndentMark = (
    <path
      d="M1.38 2.735l3.942 3.639c.383.353.973.353 1.356 0l3.943-3.64c.405-.374.43-1.006.056-1.412-.19-.205-.456-.322-.735-.322H2.058c-.553 0-1 .448-1 1 0 .28.116.545.321.735z"
      fill={editorLoading ? theme.palette.action.disabled : theme.palette.primary.main}
    />
  );

  return (
    <SvgIcon
      className={classes.indentationMarker}
      data-indentation-marker={name}
      style={{ transform: `translateX(${inToPx(position)}px)`, ...style }}
      viewBox="0 0 13 7"
      onMouseDown={(event): void => {
        if (editorLoading) return;

        onMouseDown(event);
      }}
    >
      {variant === 'default' ? leftRightIndentMark : firstLineIndentMark}
    </SvgIcon>
  );
}

function dragIndentation(editor: Editor, side: 'left' | 'right') {
  return (delta: number): number => {
    const pageDimensions = Queries.activePageDimensions(editor, 'in');
    const margin = side === 'left' ? 'marginLeft' : 'marginRight';
    const indentationProperties = Queries.indentationProperties(editor);
    const newIndentation = roundToOneSixteenthOfAnInch(indentationProperties[side] + pxToIn(delta));
    const oppositeIndentation = Math.max(
      0,
      indentationProperties[side === 'left' ? 'right' : 'left']
    );
    const firstLine = Math.max(0, indentationProperties.firstLine);
    const leftEdgePageDelta = (pageDimensions[margin] + indentationProperties[side]) * -1;

    if (pageContentWidth(pageDimensions) - oppositeIndentation - firstLine - newIndentation < 1) {
      return roundToOneSixteenthOfAnInch(
        pageContentWidth(pageDimensions) -
          oppositeIndentation -
          firstLine -
          1 -
          indentationProperties[side]
      );
    }

    return Math.max(
      leftEdgePageDelta,
      roundToOneSixteenthOfAnInch(newIndentation - indentationProperties[side])
    );
  };
}

function stopIndentationDrag(
  editor: Editor,
  editingMode: EditingMode | null,
  authorId: UUID | null,
  indentationKey: IndentationKey
) {
  return (delta: number): void => {
    if (!editor.selection || !authorId || !editingMode) return;

    const [start] = Range.edges(editor.selection);
    const AuthorizedTransforms = getAuthorizedTransformsForEditor(editor);
    const startParagraphEntry = Editor.above<ParagraphElement>(editor, {
      at: start,
      match: isParagraphElement,
    });
    if (!startParagraphEntry) return;

    const [startParagraph] = startParagraphEntry;

    switch (indentationKey) {
      case 'indentationFirstLine': {
        const value = roundToOneSixteenthOfAnInch(
          Queries.renderedParagraphFirstLineIndent(startParagraph) + delta
        );
        AuthorizedTransforms.setParagraphProps(editor, editingMode, authorId, {
          [indentationKey]: value,
        });
        break;
      }
      case 'indentationLeft': {
        const value = roundToOneSixteenthOfAnInch(
          Queries.renderedParagraphLeftIndent(startParagraph) + delta
        );
        AuthorizedTransforms.setParagraphProps(editor, editingMode, authorId, {
          [indentationKey]: value,
        });
        break;
      }
      case 'indentationRight': {
        const value = roundToOneSixteenthOfAnInch(
          Queries.renderedParagraphRightIndent(startParagraph) + delta
        );
        AuthorizedTransforms.setParagraphProps(editor, editingMode, authorId, {
          [indentationKey]: value,
        });
        break;
      }
    }
  };
}

function useDragLeftMargin() {
  const editor = useEditor();
  const currentUserId = useCurrentUserId();
  const { editingMode } = useEditingMode();
  const pageDimensions = useEditorTabStateSelector(
    state => state.derived.documentProperties.pageDimensions,
    equals
  );
  const { delta: leftMarginDelta, start: dragLeftMargin } = useMarginAdjuster(
    editor,
    editingMode,
    currentUserId,
    'horizontal',
    'start'
  );
  const safeDelta = leftMarginDelta ?? 0;

  return {
    dragLeftMargin,
    leftMarginDelta,
    leftMarginDragPosition: pageDimensions.marginLeft + safeDelta,
  };
}

function useDragRightMargin() {
  const editor = useEditor();
  const currentUserId = useCurrentUserId();
  const { editingMode } = useEditingMode();
  const pageDimensions = useEditorTabStateSelector(
    state => state.derived.documentProperties.pageDimensions,
    equals
  );
  const { delta: rightMarginDelta, start: dragRightMargin } = useMarginAdjuster(
    editor,
    editingMode,
    currentUserId,
    'horizontal',
    'end'
  );
  const safeDelta = rightMarginDelta ?? 0;

  return {
    dragRightMargin,
    rightMarginDelta,
    rightMarginDragPosition: pageDimensions.marginRight + safeDelta,
  };
}

function useDragIndentationLeft(leftMarginDelta: number) {
  const editor = useEditor();
  const currentUserId = useCurrentUserId();
  const { editingMode } = useEditingMode();
  const leftMarkerPosition = useEditorTabStateSelector(
    state => state.derived.indentationMarkerOffsets.left
  );
  const { delta: indentationLeftDelta, start: dragIndentationLeft } = useDragAndDrop({
    direction: 'horizontal',
    onDrag: dragIndentation(editor, 'left'),
    onStop: stopIndentationDrag(editor, editingMode, currentUserId, 'indentationLeft'),
  });
  const safeDelta = indentationLeftDelta ?? 0;

  return {
    dragIndentationLeft,
    indentationLeftDelta,
    leftMarkerDragPosition: leftMarginDelta + leftMarkerPosition + safeDelta,
  };
}

function useDragIndentationRight(rightMarginDelta: number) {
  const editor = useEditor();
  const currentUserId = useCurrentUserId();
  const { editingMode } = useEditingMode();
  const rightMarkerPosition = useEditorTabStateSelector(
    state => state.derived.indentationMarkerOffsets.right
  );
  const { delta: indentationRightDelta, start: dragIndentationRight } = useDragAndDrop({
    direction: 'horizontal',
    onDrag: (delta: number) => dragIndentation(editor, 'right')(delta * -1),
    onStop: stopIndentationDrag(editor, editingMode, currentUserId, 'indentationRight'),
  });
  const safeDelta = indentationRightDelta ?? 0;
  const rightMarkerDragPosition = useMemo(() => {
    const defaultRightMarkerDragPosition = rightMarkerPosition - rightMarginDelta - safeDelta;
    if (!editor.selection) return defaultRightMarkerDragPosition;

    const [start] = Range.edges(editor.selection);
    const isStartInTableCell = Boolean(
      Editor.above<TableCellElement>(editor, {
        at: start,
        match: isTableCellElement,
      })
    );
    return isStartInTableCell ? rightMarkerPosition - safeDelta : defaultRightMarkerDragPosition;
  }, [editor, rightMarginDelta, rightMarkerPosition, safeDelta]);

  return {
    dragIndentationRight,
    indentationRightDelta,
    rightMarkerDragPosition,
  };
}

export function useDragFirstLineIndent(leftMarkerDragPosition: number) {
  const editor = useEditor();
  const currentUserId = useCurrentUserId();
  const { editingMode } = useEditingMode();
  const firstLine = useEditorTabStateSelector(
    state => state.derived.indentationProperties.firstLine
  );
  const { delta: firstLineIndentDelta, start: dragFirstLineIndent } = useDragAndDrop({
    direction: 'horizontal',
    onDrag: delta => {
      const deltaInIn = pxToIn(delta);
      const pageDimensions = Queries.activePageDimensions(editor, 'in');
      const { firstLine, left, right } = Queries.indentationProperties(editor);
      const newIndentation = roundToOneSixteenthOfAnInch(firstLine + deltaInIn);
      // Ensure firstLineIndent doesn't go outside left page edge...
      const leftEdgePageDelta = (pageDimensions.marginLeft + left + firstLine) * -1;

      if (pageContentWidth(pageDimensions) - left - right - newIndentation < 1) {
        return pageContentWidth(pageDimensions) - left - right - 1 - firstLine;
      }

      return Math.max(leftEdgePageDelta, deltaInIn);
    },
    onStop: stopIndentationDrag(editor, editingMode, currentUserId, 'indentationFirstLine'),
  });
  const safeDelta = firstLineIndentDelta ?? 0;

  return {
    dragFirstLineIndent,
    firstLineIndentDelta,
    firstLineMarkerDragPosition: leftMarkerDragPosition + firstLine + safeDelta,
  };
}
